// listFeaturedBusinessProperty.jsx

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    GetFeaturedBusinessPropertyList,
    AddFeaturedBusinessProperty,
    UpdateFeaturedBusinessProperty,
} from "../../Redux/Actions/featuredBusinessPropertyAction";
import { columnsFeaturedBusinessPropertyList } from "../../Global Functions/columnsAntd";
import TableComponent from "../../Global Functions/tableComponents";
import toast from "react-hot-toast";
import FeaturedBusinessPropertyEditModal from "./popupFeaturedBusniessProperty";
import FeaturedBusinessPropertyViewModal from "./viewFeaturedBusniessProperty";
import DateRangePopup from "../../Utils/dateFilterPopup";
import { Button } from "antd";

const ListFeaturedBusinessProperty = () => {
    const dispatch = useDispatch();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState(null);

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedViewProperty, setSelectedViewProperty] = useState(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    const { admin } = useSelector((state) => state.admin || {});
    const accessCode = admin?.data?.access_code || [];
    const canAdd = Array.isArray(accessCode) && accessCode.includes("add");
    const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

    const featuredBusinessPropertyState = useSelector((state) => state.featuredBusinessProperty || {});
    const properties = useMemo(() => {
        return featuredBusinessPropertyState.featuredBusinessProperties || [];
    }, [featuredBusinessPropertyState.featuredBusinessProperties]);
    const propertyTotal = useMemo(() => {
        return featuredBusinessPropertyState.total || 0;
    }, [featuredBusinessPropertyState.total]);
    const { loading } = featuredBusinessPropertyState;

    const fetchDetails = useCallback(
        (showLoader = true, page = pagination.current, pageSize = pagination.pageSize) => {
            const payload = {
                page,
                limit: pageSize,
                filter: debouncedSearch,
                is_deleted: false,
                ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
                ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
            };
            return dispatch(GetFeaturedBusinessPropertyList(payload, showLoader)).catch(() => {
                if (showLoader) {
                    toast.error("Failed to fetch featured business property list");
                }
            });
        },
        [dispatch, debouncedSearch, dateRange, pagination.current, pagination.pageSize]
    );

    useEffect(() => {
        fetchDetails(true, pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize, fetchDetails]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [debouncedSearch, dateRange]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, total: propertyTotal }));
    }, [propertyTotal]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const openEditModal = useCallback((property = null) => {
        setSelectedProperty(property);
        setIsModalOpen(true);
    }, []);

    const closeEditModal = () => {
        setSelectedProperty(null);
        setIsModalOpen(false);
    };

    const openViewModal = useCallback((property = null) => {
        setSelectedViewProperty(property);
        setIsViewModalOpen(true);
    }, []);

    const closeViewModal = () => {
        setSelectedViewProperty(null);
        setIsViewModalOpen(false);
    };

    const handleSubmit = async (values) => {
        try {
            let result;
            if (selectedProperty?._id) {
                result = await dispatch(UpdateFeaturedBusinessProperty({ ...values, id: selectedProperty._id }));
            } else {
                result = await dispatch(AddFeaturedBusinessProperty(values));
            }

            if (result) {
                await fetchDetails(false, pagination.current, pagination.pageSize);
                closeEditModal();
                toast.success(selectedProperty?._id ? "Property updated successfully" : "Property added successfully");
                return { success: true };
            }
        } catch (error) {
            toast.error("Failed to save property");
            return { success: false };
        }
    };

    const handleDateRangeApply = (start, end) => {
        if (start === null && end === null) {
            setDateRange({ start: null, end: null });
        } else {
            setDateRange({ start, end });
        }
        setIsDatePopupVisible(false);
    };

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    return (
        <>
            <div style={{ position: "absolute", marginTop: "9.6%", marginLeft: "88.5%" }}>
                {canAdd && (
                    <Button
                        style={{ background: "#070C18", color: "white", padding: "18px", borderRadius: "20px" }}
                        onClick={() => openEditModal(null)}
                    >
                        Add Property
                    </Button>
                )}
            </div>

            <TableComponent
                pagination={pagination}
                setState={setSearchQuery}
                dataSource={properties}
                onCalendarClick={() => setIsDatePopupVisible(true)}
                onChange={handleTableChange}
                h3heading="Featured Business Property"
                columns={columnsFeaturedBusinessPropertyList(
                    pagination.current,
                    pagination.pageSize,
                    openViewModal,
                    openEditModal,
                    canEdit
                )}
                loading={loading}
            />

            {isModalOpen && (
                <FeaturedBusinessPropertyEditModal
                    visible={isModalOpen}
                    propertyData={selectedProperty}
                    onSave={handleSubmit}
                    onClose={closeEditModal}
                />
            )}

            {isViewModalOpen && (
                <FeaturedBusinessPropertyViewModal
                    visible={isViewModalOpen}
                    propertyData={selectedViewProperty}
                    onClose={closeViewModal}
                />
            )}

            <DateRangePopup
                visible={isDatePopupVisible}
                initialStart={dateRange.start}
                initialEnd={dateRange.end}
                onCancel={() => setIsDatePopupVisible(false)}
                onOk={handleDateRangeApply}
            />
        </>
    );
};

export default ListFeaturedBusinessProperty;